import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './VolumeChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface VolumeChartProps {
  values: number[];
  labels: string[];
  title: string;
  subtitle?: string;
  bottomTextA: string;
  bottomTextB: string;
  currentSessionId?: string;
  sessions?: Array<{ id: string; date: string | number; Volume: number; }>;
  isBinaryMode?: boolean;
  timeValues?: number[];
  sessionLabel?: string;
  useSessionNumbers?: boolean;
}

export const VolumeChart: React.FC<VolumeChartProps> = ({
  values,
  labels,
  title,
  subtitle,
  bottomTextA,
  bottomTextB,
  currentSessionId,
  sessions,
  isBinaryMode = false,
  timeValues,
  sessionLabel = "Session",
  useSessionNumbers = false
}) => {
  // Calculate dynamic point radius based on number of data points
  const calculatePointRadius = (dataCount: number) => {
    if (dataCount <= 5) return 4; // Default size for small datasets
    if (dataCount <= 10) return 3;
    if (dataCount <= 20) return 2;
    return 1; // Minimum size for large datasets
  };

  const calculateHoverRadius = (dataCount: number) => {
    const baseRadius = calculatePointRadius(dataCount);
    return Math.max(baseRadius + 2, 3); // Ensure hover is at least 2px larger and minimum 3px
  };

  const pointRadius = isBinaryMode ? 0 : calculatePointRadius(values.length);
  const pointHoverRadius = isBinaryMode ? 4 : calculateHoverRadius(values.length);
  
  const displayLabels = useSessionNumbers 
    ? values.map((_, i) => `${sessionLabel} ${i + 1}`)
    : labels;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#000',
        bodyColor: '#000',
        borderColor: '#E4E4E9',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context: any) {
            if (isBinaryMode) {
              return context.parsed.y === 1 ? 'Speaking' : 'Silent';
            }
            return `${parseFloat(context.parsed.y.toFixed(1))} db`;
          },
          title: function(context: any) {
            if (isBinaryMode && timeValues) {
              const timeIndex = context[0].dataIndex;
              const timeValue = timeValues[timeIndex];
              const minutes = Math.floor(timeValue / 60);
              const seconds = Math.floor(timeValue % 60);
              return `${minutes}:${seconds.toString().padStart(2, '0')}`;
            }
            return context[0].label;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          color: '#E4E4E9',
          lineWidth: 1,
        },
        ticks: {
          color: '#666',
          font: {
            size: 10,
          },
          callback: function(value: any, index: number) {
            if (isBinaryMode && timeValues) {
              const timeValue = timeValues[index];
              if (timeValue !== undefined) {
                const minutes = Math.floor(timeValue / 60);
                const seconds = Math.floor(timeValue % 60);
                return `${minutes}:${seconds.toString().padStart(2, '0')}`;
              }
            }
            return displayLabels[index] || value;
          }
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          color: '#E4E4E9',
          lineWidth: 1,
        },
        ticks: {
          color: '#666',
          font: {
            size: 10,
          },
          callback: function(value: any) {
            if (isBinaryMode) {
              return value === 1 ? 'Speaking' : (value === 0 ? 'Silent' : '');
            }
            return parseFloat(value.toFixed(1)) + ' db';
          },
          stepSize: isBinaryMode ? 1 : undefined,
          count: isBinaryMode ? 3 : undefined, // Show only 0, 1, and one in between for better spacing
        },
        min: isBinaryMode ? 0 : undefined,
        max: isBinaryMode ? 1 : undefined,
        border: {
          display: false,
        },
      },
    },
    elements: {
      point: {
        radius: pointRadius,
        hoverRadius: pointHoverRadius,
        backgroundColor: '#FFA825',
        borderColor: '#FFA825',
        borderWidth: 2,
      },
      line: {
        tension: isBinaryMode ? 0 : 0.4,
        borderColor: '#FFA825',
        borderWidth: 2,
        stepped: isBinaryMode ? ('before' as const) : false,
      },
    },
  };

  const data = {
    labels: displayLabels,
    datasets: [
      {
        data: values,
        borderColor: '#FFA825',
        backgroundColor: 'rgba(255, 168, 37, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Convert string values to numbers before calculation
  const numericValues = values.map(v => typeof v === 'string' ? parseFloat(v) : v);
  const sum = numericValues.reduce((a, b) => a + b, 0);
  const average = numericValues.length > 0 ? parseFloat((sum / numericValues.length).toFixed(1)) : 0;

  // Calculate loudness comparison percentage using only sessions before current session chronologically
  let louderPercentage = 0;
  if (currentSessionId && sessions && sessions.length > 1) {
    // Find current session data
    const currentSession = sessions.find(s => s.id === currentSessionId);

    if (currentSession && currentSession.Volume != null && !isNaN(parseFloat(String(currentSession.Volume)))) {
      // Convert dates to proper timestamps for comparison
      const currentSessionTimestamp = typeof currentSession.date === 'string' ? 
        new Date(currentSession.date).getTime() : // Parse ISO string directly
        (typeof currentSession.date === 'number' ? 
          (currentSession.date < 1e12 ? currentSession.date * 1000 : currentSession.date) : 
          new Date(currentSession.date).getTime());

      const currentSessionVolume = parseFloat(String(currentSession.Volume));

      // Get only sessions that occurred before the current session chronologically
      const historicalSessions = sessions.filter(session => {
        if (session.id === currentSessionId) return false; // Exclude current session

        // Convert session date to proper timestamp
        const sessionTimestamp = typeof session.date === 'string' ? 
          new Date(session.date).getTime() : // Parse ISO string directly
          (typeof session.date === 'number' ? 
            (session.date < 1e12 ? session.date * 1000 : session.date) : 
            new Date(session.date).getTime());

        const sessionVolume = parseFloat(String(session.Volume));
        const isValidVolume = session.Volume != null && !isNaN(sessionVolume);
        const isBeforeCurrent = sessionTimestamp < currentSessionTimestamp;

        console.log('Session filter check:', {
          sessionId: session.id,
          originalDate: session.date,
          sessionTimestamp,
          currentSessionTimestamp,
          volume: session.Volume,
          parsedVolume: sessionVolume,
          isValidVolume,
          isBeforeCurrent,
          include: isBeforeCurrent && isValidVolume
        });

        return isBeforeCurrent && isValidVolume;
      });

      console.log('Volume percentage calculation:', {
        currentSessionId,
        currentSessionVolume,
        currentSessionTimestamp,
        historicalSessions: historicalSessions.map(s => ({ id: s.id, date: s.date, Volume: s.Volume })),
        totalSessions: sessions.length
      });

      if (historicalSessions.length > 0) {
        const historicalAverage = historicalSessions.reduce((sum, session) => sum + parseFloat(String(session.Volume)), 0) / historicalSessions.length;

        console.log('Historical average calculation:', {
          historicalSessions: historicalSessions.map(s => ({ id: s.id, Volume: s.Volume, parsedVolume: parseFloat(String(s.Volume)) })),
          sumOfVolumes: historicalSessions.reduce((sum, session) => sum + parseFloat(String(session.Volume)), 0),
          count: historicalSessions.length,
          historicalAverage
        });

        if (historicalAverage > 0) {
          const rawPercentage = ((currentSessionVolume - historicalAverage) / historicalAverage) * 100;
          louderPercentage = parseFloat(rawPercentage.toFixed(2));

          console.log('Volume calculation details:', {
            currentSessionVolume,
            historicalAverage,
            rawPercentage,
            finalPercentage: louderPercentage
          });
        } else {
          console.log('Historical average is 0 or negative, cannot calculate percentage:', historicalAverage);
        }
      } else {
        console.log('No historical sessions found for percentage calculation');
      }
    }
  }

  return (
    <div className="volume-chart">
      <div className="volume-chart__header">
        <div className="volume-chart__title">{title}</div>
        {subtitle && (
          <div className="volume-chart__subtitle">
            {subtitle
              .replace('[percent]', Math.abs(louderPercentage).toFixed(2))
              .replace('громче', louderPercentage >= 0 ? 'громче' : 'тише')
              .replace('louder', louderPercentage >= 0 ? 'louder' : 'quieter')
              .replace('голосніше', louderPercentage >= 0 ? 'голосніше' : 'тихіше')
            }
          </div>
        )}
        {!isBinaryMode && (
          <div className="volume-chart__average">
            <span className="volume-chart__average-label">{bottomTextA}:</span>
            <span className="volume-chart__average-value">{isNaN(average) ? '0' : average} {bottomTextB}</span>
          </div>
        )}
      </div>

      <div className="volume-chart__container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};